import { apiClient } from '../../utils/api';
import {
  TenantWithMetadata,
  CreateTenantRequest,
  UpdateTenantRequest,
  UpdateTenantLimitsRequest,
  PaginationInfo
} from '../../types/super-admin';

export interface ListTenantsRequest {
  page?: number;
  limit?: number;
  status?: string;
  subscriptionTier?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: string;
}

export interface ListTenantsResponse {
  tenants: TenantWithMetadata[];
  pagination: PaginationInfo;
  summary: {
    totalTenants: number;
    activeTenants: number;
    suspendedTenants: number;
    totalUsers: number;
    totalContent: number;
    totalCampaigns: number;
  };
}

export interface TenantStats {
  userCount: number;
  contentCount: number;
  campaignCount: number;
  storageUsed: number;
  bandwidthUsed: number;
  lastActivity: string;
  recentActivity: Array<{
    type: string;
    count: number;
    date: string;
  }>;
}

export class TenantService {
  private static readonly BASE_PATH = '/super-admin/tenants';

  static async listTenants(params: ListTenantsRequest = {}): Promise<ListTenantsResponse> {
    const queryParams = new URLSearchParams();
    
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.status) queryParams.append('status', params.status);
    if (params.subscriptionTier) queryParams.append('subscriptionTier', params.subscriptionTier);
    if (params.search) queryParams.append('search', params.search);
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

    const url = `${this.BASE_PATH}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return apiClient.get<ListTenantsResponse>(url);
  }

  static async getTenant(tenantId: string): Promise<TenantWithMetadata> {
    return apiClient.get<TenantWithMetadata>(`${this.BASE_PATH}/${tenantId}`);
  }

  static async createTenant(tenantData: CreateTenantRequest): Promise<TenantWithMetadata> {
    return apiClient.post<TenantWithMetadata>(this.BASE_PATH, tenantData);
  }

  static async updateTenant(tenantId: string, updates: UpdateTenantRequest): Promise<TenantWithMetadata> {
    return apiClient.put<TenantWithMetadata>(`${this.BASE_PATH}/${tenantId}`, updates);
  }

  static async deleteTenant(tenantId: string): Promise<void> {
    return apiClient.delete<void>(`${this.BASE_PATH}/${tenantId}`);
  }

  static async suspendTenant(tenantId: string, reason?: string): Promise<TenantWithMetadata> {
    return apiClient.put<TenantWithMetadata>(`${this.BASE_PATH}/${tenantId}/suspend`, { reason });
  }

  static async activateTenant(tenantId: string): Promise<TenantWithMetadata> {
    return apiClient.put<TenantWithMetadata>(`${this.BASE_PATH}/${tenantId}/activate`, {});
  }

  static async updateTenantLimits(tenantId: string, limits: UpdateTenantLimitsRequest): Promise<TenantWithMetadata> {
    return apiClient.put<TenantWithMetadata>(`${this.BASE_PATH}/${tenantId}/limits`, limits);
  }

  static async getTenantStats(tenantId: string): Promise<TenantStats> {
    return apiClient.get<TenantStats>(`${this.BASE_PATH}/${tenantId}/stats`);
  }

  static async getTenantUsers(tenantId: string, page = 1, limit = 20): Promise<{
    users: Array<{
      id: string;
      email: string;
      firstName: string;
      lastName: string;
      role: string;
      status: string;
      lastLogin?: string;
      createdAt: string;
    }>;
    pagination: PaginationInfo;
  }> {
    return apiClient.get<any>(`${this.BASE_PATH}/${tenantId}/users?page=${page}&limit=${limit}`);
  }

  static async exportTenants(filters?: ListTenantsRequest): Promise<{ downloadUrl: string }> {
    const queryParams = new URLSearchParams();
    
    if (filters?.status) queryParams.append('status', filters.status);
    if (filters?.subscriptionTier) queryParams.append('subscriptionTier', filters.subscriptionTier);
    if (filters?.search) queryParams.append('search', filters.search);

    const url = `${this.BASE_PATH}/export${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return apiClient.get<{ downloadUrl: string }>(url);
  }

  static async getTenantAuditHistory(tenantId: string, page = 1, limit = 20): Promise<{
    logs: Array<{
      id: string;
      timestamp: string;
      action: string;
      changes: Record<string, any>;
      performedBy: string;
    }>;
    pagination: PaginationInfo;
  }> {
    return apiClient.get<any>(`${this.BASE_PATH}/${tenantId}/audit-history?page=${page}&limit=${limit}`);
  }

  static async validateTenantDomain(domain: string): Promise<{ available: boolean; suggestions?: string[] }> {
    return apiClient.post<{ available: boolean; suggestions?: string[] }>(`${this.BASE_PATH}/validate-domain`, { domain });
  }

  static async getTenantUsageReport(tenantId: string, startDate: string, endDate: string): Promise<{
    period: { start: string; end: string };
    usage: {
      users: { active: number; total: number };
      content: { created: number; views: number; storage: number };
      campaigns: { active: number; impressions: number; clicks: number };
      bandwidth: { used: number; limit: number };
    };
    costs: {
      storage: number;
      bandwidth: number;
      users: number;
      total: number;
    };
  }> {
    return apiClient.get<any>(`${this.BASE_PATH}/${tenantId}/usage-report?startDate=${startDate}&endDate=${endDate}`);
  }
}